import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LayoutGrid } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { PRODUCTS } from "@/data/content";
import { ProductIcon } from "@/lib/icons";

const NAV_KEYS = [
  { to: "/", key: "home" },
  { to: "/products", key: "products" },
  { to: "/industries", key: "industries" },
  { to: "/pricing", key: "pricing" },
  { to: "/why-us", key: "whyUs" },
  { to: "/contact", key: "contact" },
];

const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-slate-700 bg-slate-900/80 p-0.5 text-xs font-semibold">
      {["en", "bm"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 rounded-full transition-colors duration-200 ${lang === l ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-200"}`}
        >
          {l === "en" ? "EN" : "BM"}
        </button>
      ))}
    </div>
  );
};

const AppSwitcherMenu = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const { t, pick } = useLanguage();
  return (
    <div className="relative">
      <button
        data-testid="app-switcher-btn"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-slate-800/60"
      >
        <LayoutGrid className="w-4 h-4" /> {t("nav.apps")}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="app-switcher-menu" className="absolute left-0 top-full mt-2 z-50 w-72 glass rounded-2xl p-3 shadow-2xl shadow-black/50 grid grid-cols-2 gap-1.5 animate-fade-up">
            {PRODUCTS.map((p) => (
              <button
                key={p.slug}
                data-testid={`switcher-${p.slug}`}
                onClick={() => { setOpen(false); onNavigate(`/products/${p.slug}`); }}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-xs font-medium text-slate-300 hover:bg-slate-800/80 border border-transparent ${p.accent.border} transition-colors`}
              >
                <span className={`${p.accent.text}`}><ProductIcon name={p.icon} className="w-4 h-4" /></span>
                <span>{p.short}</span>
              </button>
            ))}
            <div className="col-span-2 px-3 pt-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono2">{pick({ en: "One login. All apps.", bm: "Satu log masuk. Semua aplikasi." })}</div>
          </div>
        </>
      )}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header data-testid="main-nav" className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/90 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link to="/" data-testid="nav-logo" className="flex items-center mr-2">
            <img src="/logo-full.png" alt="OTOSUBS — Autonomous Business Solution" className="h-10 w-auto" />
            <span data-testid="nav-logo-tagline" className="hidden 2xl:block ml-3 pl-3 border-l border-slate-700 text-[10px] font-mono2 uppercase tracking-[0.18em] text-slate-400 leading-tight">
              Autonomous<br />Business Solution
            </span>
          </Link>
          <div className="hidden lg:block">
            <AppSwitcherMenu onNavigate={(p) => navigate(p)} />
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_KEYS.map(({ to, key }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `px-2.5 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors ${isActive ? "text-white bg-slate-800/80" : "text-slate-400 hover:text-white hover:bg-slate-800/50"}`
              }
            >
              {t(`nav.${key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LangToggle />
          <Link to="/login" data-testid="nav-login" className="text-[13px] font-medium text-slate-300 hover:text-white px-2.5 py-2 whitespace-nowrap transition-colors">
            {t("nav.login")}
          </Link>
          {user && (
            <Link to="/admin" data-testid="nav-admin" className="text-[13px] font-semibold text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 rounded-full px-4 py-2 whitespace-nowrap transition-colors">
              Admin
            </Link>
          )}
          <Link to="/contact" data-testid="nav-book-demo" className="hidden xl:inline-flex text-[13px] font-semibold text-slate-200 border border-slate-700 hover:border-slate-500 rounded-full px-4 py-2 whitespace-nowrap transition-colors">
            {t("nav.bookDemo")}
          </Link>
          <Link to="/signup" data-testid="nav-start-free" className="text-[13px] font-semibold text-white bg-indigo-500 hover:bg-indigo-400 rounded-full px-4 py-2 whitespace-nowrap transition-colors shadow-lg shadow-indigo-500/30">
            {t("nav.startFree")}
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <LangToggle />
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-slate-300">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-800 bg-slate-900/95 backdrop-blur-xl px-5 py-4 space-y-1 animate-fade-up">
          {NAV_KEYS.map(({ to, key }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`mobile-nav-${key}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "text-white bg-slate-800" : "text-slate-300"}`
              }
            >
              {t(`nav.${key}`)}
            </NavLink>
          ))}
          <div className="flex gap-2 pt-3">
            <Link to="/login" data-testid="mobile-nav-login" onClick={() => setMobileOpen(false)} className="flex-1 text-center text-sm font-medium text-slate-200 border border-slate-700 rounded-full px-4 py-2.5">
              {t("nav.login")}
            </Link>
            <Link to="/signup" data-testid="mobile-nav-start-free" onClick={() => setMobileOpen(false)} className="flex-1 text-center text-sm font-semibold text-white bg-indigo-500 rounded-full px-4 py-2.5">
              {t("nav.startFree")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
